<script lang="ts" setup>
import { useWebSocketBlockMode } from '~/composables/unified/DevPanel/useWebSocketBlockMode';
import { useWebSocketChaosMode } from '~/composables/unified/DevPanel/useWebSocketChaosMode';
import { useWebSocketTestMode } from '~/composables/unified/DevPanel/useWebSocketTestMode';

const testMode = useWebSocketTestMode();
const blockMode = useWebSocketBlockMode(testMode);
const chaosMode = useWebSocketChaosMode(testMode);

const pusherConnection = usePusherConnection();

const connectionStatusDotStyles = cva('size-2 rounded-full', {
  variants: {
    status: {
      connected: 'bg-secondary',
      connecting: 'bg-brand-yellow animate-pulse',
      disconnected: 'bg-destructive',
      unavailable: 'bg-destructive',
      failed: 'bg-destructive',
      initialized: 'bg-muted-foreground',
    },
  },
});

const connectionStatusPillStyles = cva('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full', {
  variants: {
    status: {
      connected: 'bg-secondary-subtle',
      connecting: 'bg-brand-yellow-subtle',
      disconnected: 'bg-destructive-subtle',
      unavailable: 'bg-destructive-subtle',
      failed: 'bg-destructive-subtle',
      initialized: 'bg-muted',
    },
  },
});

const operatorButtonStyles = cva('rounded-md px-2 py-0.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed', {
  variants: {
    variant: {
      kill: 'text-white bg-destructive hover:bg-destructive/90',
      connect: 'text-primary-foreground bg-primary hover:brightness-90',
      chaos: 'text-foreground bg-brand-orange hover:bg-brand-orange/90',
      block: 'text-foreground bg-brand-purple hover:bg-brand-purple/90',
      active: 'text-white bg-destructive hover:bg-destructive/90',
    },
  },
});

const CONNECTION_STATUS_LABELS = {
  connected: 'Connected',
  connecting: 'Connecting',
  disconnected: 'Disconnected',
  unavailable: 'Unavailable',
  failed: 'Failed',
  initialized: 'Initialized',
} as const;

type ConnectionState = keyof typeof CONNECTION_STATUS_LABELS;

const connectionState = computed<ConnectionState>(() =>
  (pusherConnection?.connectionState.value ?? 'initialized') as ConnectionState,
);

const connectionStatus = computed(() => ({
  dotClasses: connectionStatusDotStyles({ status: connectionState.value }),
  pillClasses: connectionStatusPillStyles({ status: connectionState.value }),
  label: CONNECTION_STATUS_LABELS[connectionState.value] ?? CONNECTION_STATUS_LABELS.initialized,
}));

const chaosButtonClasses = computed(() =>
  operatorButtonStyles({ variant: chaosMode?.isActive.value ? 'active' : 'chaos' }),
);

const blockButtonClasses = computed(() =>
  operatorButtonStyles({ variant: blockMode?.isActive.value ? 'active' : 'block' }),
);

const isConnected = computed(() => pusherConnection?.isConnected.value ?? false);

const canKill = computed(() => isConnected.value);
const canConnect = computed(() => !isConnected.value);

const activeSubscriptions = computed(() => {
  if (!pusherConnection?.activeSubscriptions.value) {
    return [];
  }
  return Array.from(pusherConnection.activeSubscriptions.value.values());
});

const channelStatusDotStyles = cva('size-2 rounded-full flex-shrink-0', {
  variants: {
    status: {
      pending: 'bg-muted-foreground',
      subscribing: 'bg-brand-yellow animate-pulse',
      active: 'bg-secondary',
      disconnected: 'bg-destructive',
      reconnecting: 'bg-brand-blue animate-pulse',
      error: 'bg-destructive',
    },
  },
});

const CHANNEL_STATUS_LABELS = {
  pending: 'Pending',
  subscribing: 'Subscribing...',
  active: '',
  disconnected: 'Disconnected',
  reconnecting: 'Reconnecting...',
  error: 'Error',
} as const;
</script>

<template>
  <div class="px-3 py-1">
    <div class="flex items-center gap-2">
      <span :class="connectionStatus.pillClasses">
        <span :class="connectionStatus.dotClasses" />
        <span class="text-xs font-medium text-foreground">{{ connectionStatus.label }}</span>
      </span>
    </div>

    <div class="mt-2 flex flex-col gap-2">
      <div v-if="activeSubscriptions.length > 0">
        <div class="mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Channels
        </div>
        <div class="flex flex-col gap-1">
          <div
            v-for="sub in activeSubscriptions"
            :key="sub.id"
            class="flex items-center gap-1.5 text-xs"
          >
            <span :class="channelStatusDotStyles({ status: sub.status })" />
            <span class="truncate text-foreground">{{ sub.operationName || 'Unknown' }}</span>
            <span v-if="CHANNEL_STATUS_LABELS[sub.status]" class="text-[11px] text-muted-foreground">
              {{ CHANNEL_STATUS_LABELS[sub.status] }}
            </span>
          </div>
        </div>
      </div>

      <div>
        <div class="mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Dev Tools
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <button
            :class="operatorButtonStyles({ variant: 'kill' })"
            :disabled="!canKill"
            title="Force disconnect"
            @click="testMode?.forceDisconnect()"
          >
            Kill
          </button>
          <button
            :class="operatorButtonStyles({ variant: 'connect' })"
            :disabled="!canConnect"
            title="Force reconnect"
            @click="testMode?.forceReconnect()"
          >
            Connect
          </button>
          <button
            :class="chaosButtonClasses"
            :disabled="blockMode?.isActive.value && !chaosMode?.isActive.value"
            title="Drop coach feed frames at random so gap detection fires"
            @click="chaosMode?.toggle()"
          >
            {{ chaosMode?.isActive.value ? 'Stop Chaos' : 'Chaos' }}
          </button>
          <button
            :class="blockButtonClasses"
            :disabled="chaosMode?.isActive.value && !blockMode?.isActive.value"
            title="Suspend the coach feed so lifting it proves the catch-up read"
            @click="blockMode?.toggle()"
          >
            {{ blockMode?.isActive.value ? `Stop (${blockMode.duration.value}s)` : 'Block' }}
          </button>
        </div>
        <div v-if="chaosMode?.isActive.value || blockMode?.isActive.value" class="mt-0.5 text-xs text-muted-foreground">
          <span v-if="chaosMode?.isActive.value">Chaos active - dropping coach feed frames</span>
          <span v-if="blockMode?.isActive.value">Coach feed suspended...</span>
        </div>
      </div>
    </div>
  </div>
</template>
